import { useContext, useMemo } from "react";
import { Outlet } from "react-router-dom";
import { ThemeProvider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import RewampAppBar from "../components/RewampAppBar";
import { RewampFooter } from "../components/RewampFooter";
import isStandalone from "../../Utils/isStandalone";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";
import generateRewampTheme from "../rewampTheme";

/**
 * RewampLayout — same AppAppBar + Footer as Default, but wrapped with the
 * Rewamp ThemeProvider (Bihar Krishi Figma style guide).
 *
 * The inner ThemeProvider overrides the outer one from App.js for all children,
 * so AppAppBar, Footer, and page components automatically adopt the new palette
 * (Arab Green primary, Noto Sans, #F5F2EB background, etc.).
 *
 * ThemeContext (ourTheme / toggleTheme) still flows through from App.js since
 * it is a separate React context above both ThemeProviders.
 */
export default function RewampLayout() {
  const {
    handleLogout,
    decreaseFontSize,
    increaseFontSize,
    resetTextSize,
    ourTheme,
    initData,
    fontSize,
  } = useContext(AppLayoutContext);

  const isMdUp = useMediaQuery(useTheme().breakpoints.up("md"));

  // Regenerates only when fontSize or ourTheme changes — same pattern as App.js
  const rewampTheme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme]
  );

  return (
    <ThemeProvider theme={rewampTheme}>
      <RewampAppBar
        handleLogout={handleLogout}
        decreaseFontSize={decreaseFontSize}
        increaseFontSize={increaseFontSize}
        ourTheme={ourTheme}
        resetTextSize={resetTextSize}
      />
      {/* tabIndex=-1 makes the landmark programmatically focusable so the
          "Skip to main content" link can move focus here (a bare <main> is
          not focusable, so .focus() would silently no-op). outline:none keeps
          the whole region from getting a focus ring on this non-keyboard focus. */}
      <main
        className={`app-body ${isMdUp ? "app-body-md" : ""}`}
        id="main-portal"
        tabIndex={-1}
        style={{ outline: "none" }}
      >
        <Outlet />
      </main>
      {!isStandalone() && <RewampFooter initData={initData} />}
    </ThemeProvider>
  );
}
